import { apiClient } from './client';
import { API_ENDPOINTS } from '@/constants/api';
import type { CreateInviteRequest, CreateInviteResponse } from '@/types/api';
import type { InviteData } from '@/types/invite';

/**
 * Тип для обновления приглашения
 */
export type UpdateInviteRequest = Partial<CreateInviteRequest>;

/**
 * Тип ответа при обновлении приглашения
 */
export type UpdateInviteResponse = {
  success: boolean;
  invite: InviteData;
};

/**
 * Тип ответа при удалении приглашения
 */
export type DeleteInviteResponse = {
  success: boolean;
  message: string;
};

/**
 * API методы для работы с приглашениями
 */
export const inviteApi = {
  /**
   * Создает новое приглашение
   * @param data - Данные приглашения
   * @returns Promise с данными созданного приглашения
   */
  create: async (data: CreateInviteRequest): Promise<CreateInviteResponse> => (
    apiClient.post<CreateInviteResponse>(API_ENDPOINTS.CREATE_INVITE, data)
  ),

  /**
   * Получает данные приглашения по ID
   * @param id - ID приглашения
   * @returns Promise с данными приглашения
   */
  getById: async (id: string): Promise<InviteData> => apiClient.get<InviteData>(API_ENDPOINTS.GET_INVITE(id)),

  /**
   * Получает все приглашения пользователя по Telegram ID
   * @param telegramUserId - ID пользователя в Telegram
   * @returns Promise с массивом приглашений
   */
  getByTelegramUserId: async (telegramUserId: string): Promise<InviteData[]> => (
    apiClient.get<InviteData[]>(API_ENDPOINTS.GET_USER_INVITES(telegramUserId))
  ),

  /**
   * Обновляет приглашение (только для админов)
   * @param id - ID приглашения
   * @param data - Данные для обновления
   * @returns Promise с обновленными данными приглашения
   */
  update: async (id: string, data: UpdateInviteRequest): Promise<UpdateInviteResponse> => (
    apiClient.put<UpdateInviteResponse>(API_ENDPOINTS.ADMIN_UPDATE_INVITE(id), data)
  ),

  /**
   * Удаляет приглашение (только для админов)
   * @param id - ID приглашения
   * @returns Promise с результатом удаления
   */
  delete: async (id: string): Promise<DeleteInviteResponse> => (
    apiClient.delete<DeleteInviteResponse>(API_ENDPOINTS.ADMIN_DELETE_INVITE(id))
  ),
};
